import {
	extractYouTubeVideoId,
	YouTubeEmbed,
	YouTubeFrame,
	YouTubePending,
	YouTubePlaceholder,
} from '@worldcoo/ui/components/youtube-embed';
import { useUsercentricsServiceConsent } from '@/lib/use-usercentrics-consent';
import { onUsercentricsConsentChanged } from '@worldcoo/ui/lib/usercentrics';
import { useLingui } from '@lingui/react/macro';
import { lazy, Suspense, useEffect, useState } from 'react';

/**
 * Our own player is the heavy half of this component and only reachable for a non-YouTube URL, so it is the
 * part behind the boundary. This module itself is imported STATICALLY by the campaign hero and cards, and
 * that is load-bearing: it used to be the lazy one, which meant hydration reached a component whose chunk
 * had not arrived, React rendered the Suspense fallback, and a video the server had already rendered
 * flickered back to its poster and forward again. A statically imported component hydrates in place.
 */
const SelfHostedVideo = lazy(() => import('./self-hosted-video'));

/**
 * Name of the YouTube service in the Usercentrics configuration. Must match it exactly; if it ever stops
 * matching, `useUsercentricsServiceConsent` reports `denied` and the video falls back to click-to-load —
 * degraded, never leaky.
 */
const YOUTUBE_SERVICE_NAME = 'YouTube Video';

export function VideoEmbed(props: { url: string; title?: string; className?: string; posterSrc?: string }) {
	const { t } = useLingui();
	// Only YouTube is gated. The other branch is our own player streaming from our own CDN, so there is no
	// third party to consent to.
	const youTubeConsent = useUsercentricsServiceConsent(YOUTUBE_SERVICE_NAME);
	const [activatedByDonor, setActivatedByDonor] = useState(false);
	const isYouTube = extractYouTubeVideoId(props.url) !== null;

	// A fresh answer from the CMP supersedes the one-off click. Without this, a donor who pressed
	// "Play video" and then went to Privacy Settings to refuse YouTube kept the player — the revocation had
	// no visible effect on the single embed it was most obviously aimed at. Any change is enough to drop the
	// override: an explicit trip through the banner is a decision, and it is the decision that should stand.
	//
	// Unmounting the iframe is the whole of what we can do. Cookies YouTube has already set live on
	// `youtube-nocookie.com` and cannot be cleared from this origin; stopping the embed is what stops more.
	useEffect(() => onUsercentricsConsentChanged(() => setActivatedByDonor(false)), []);

	if (isYouTube) {
		if (youTubeConsent === 'denied' && !activatedByDonor) {
			return (
				<YouTubePlaceholder
					posterSrc={props.posterSrc}
					title={props.title}
					className={props.className}
					texts={{
						activate: t`Play video`,
						notice: t`Playing it loads content from YouTube, which may set cookies on your device.`,
					}}
					onActivate={() => setActivatedByDonor(true)}
				/>
			);
		}

		// Nothing decided yet — first visit, or a donor whose choice we could not read. The poster is what the
		// hero would have shown anyway, so there is nothing to flash.
		if (youTubeConsent === 'unknown' && !activatedByDonor) {
			return <YouTubePending posterSrc={props.posterSrc} title={props.title} className={props.className} />;
		}

		// Consent is known before the first byte of HTML — the client mirrors the CMP's decision into a cookie
		// and the server reads it — so the player is server-rendered for a donor who already accepted, and
		// hydrates in place with nothing to swap. See `syncConsentCookie`.
		//
		// No poster is passed on purpose: the hero image is the fallback for a campaign without a playable
		// video, not something to show while a playable one loads. See `YouTubeEmbed`.
		//
		// `autoPlay` only on the donor's own click: that control says "Play video", so it has to play one.
		// Consent arriving from the banner instead is not a request to watch anything, so it mounts paused.
		return <YouTubeEmbed url={props.url} title={props.title} className={props.className} autoPlay={activatedByDonor} />;
	}

	return (
		<Suspense fallback={<YouTubeFrame posterSrc={props.posterSrc} title={props.title} className={props.className} />}>
			<SelfHostedVideo url={props.url} title={props.title} className={props.className} />
		</Suspense>
	);
}
